import { useTranslation } from 'react-i18next';
import isEqual from 'lodash/isEqual';

import { useDebounce } from 'hooks/useDebounce';
import { usePaymentReconciliations } from 'services/repositories/paymentReconciliation/paymentReconciliation';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { formatCurrency } from 'support/helpers/currency/currency';
import { formatDay } from 'support/helpers/dateTime/dateTime';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { PaymentReconciliationRoutes, PaymentReconciliationStatusEnum } from 'support/types';

import { PaymentReconciliationFilterKey } from 'pages/PaymentReconciliation/filters';
import { TabContentProps } from 'pages/PaymentReconciliation/types';
import { InfiniteScroll } from 'components/ListTable/InfiniteScroll/InfiniteScroll';
import { ListTable } from 'components/ListTable/ListTable';
import { TableHeader, TableItem } from 'components/ListTable/types';
import { ProgressBar } from 'components/ProgressBar/ProgressBar';

import { SubmitToErpAction } from '../SubmitToErpAction/SubmitToErpAction';

export const PaymentReconciliationOpenTabContent = ({ filterValues, clearAllFilters }: TabContentProps) => {
  const { t } = useTranslation();
  const hasFilters = filterValues && filterValues.length > 0;
  const debouncedFilterValues = useDebounce(filterValues, 150);
  const differentFilters = !isEqual(debouncedFilterValues, filterValues);

  const {
    data: paymentReconciliations,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
  } = usePaymentReconciliations({
    variables: {
      query: {
        filterValues: [
          ...debouncedFilterValues,
          {
            key: PaymentReconciliationFilterKey.payment_reconciliation_status,
            value: PaymentReconciliationStatusEnum.OPEN,
          },
        ],
      },
    },
  });

  const tableHeaders: Array<TableHeader> = [
    { label: t('paymentReconciliation:listPage.openTab.list.tableHeaders.documentExternalRef') },
    { label: t('paymentReconciliation:listPage.openTab.list.tableHeaders.documentDate') },
    { label: t('paymentReconciliation:listPage.openTab.list.tableHeaders.buyer') },
    { label: t('paymentReconciliation:listPage.openTab.list.tableHeaders.amount'), alignRight: true },
    { label: t('paymentReconciliation:listPage.openTab.list.tableHeaders.progress') },
    { key: 'actions', label: '', invisible: true, alignRight: true },
  ];

  const data = paymentReconciliations?.pages?.flatMap((page) => page.data);
  type ReconciliationItem = NonNullable<typeof data>[number];

  const emptyData = data?.length === 0;

  const buildRow = (reconciliationItem: ReconciliationItem): Array<TableItem> => {
    const partner = reconciliationItem.buyer;

    return [
      {
        element: reconciliationItem.external_message_ref || EMPTY_FIELD,
        className: 'max-w-xs truncate',
        key: `external-ref-${reconciliationItem.external_message_ref}`,
      },

      {
        element: (
          <span className="tabular-nums">
            {reconciliationItem.external_document_date
              ? formatDay(reconciliationItem.external_document_date)
              : EMPTY_FIELD}
          </span>
        ),
        key: 'created-at',
      },
      {
        element: partner?.name ?? EMPTY_FIELD,
        key: 'partner-name',
        className: 'max-w-xs truncate',
      },
      {
        element: (
          <span className="tabular-nums">
            {typeof reconciliationItem.remitted_amount === 'number'
              ? formatCurrency(reconciliationItem.remitted_amount, reconciliationItem.currency)
              : EMPTY_FIELD}
          </span>
        ),
        className: 'text-end',
        key: 'amount',
      },
      {
        //We are using Math.floor to ensure it is an integer and that 99.5 rounds down to 99 instead of up to 100.
        element:
          typeof reconciliationItem.progress === 'number' ? (
            <ProgressBar progress={Math.floor(reconciliationItem.progress)} />
          ) : (
            EMPTY_FIELD
          ),
        key: 'progress',
        className: 'tabular-nums w-64',
      },
      {
        element: (
          <div className="w-full text-center">
            <SubmitToErpAction
              numberOfDocuments={reconciliationItem.number_of_line_items}
              receiverEnvelope={reconciliationItem.receiver_envelope}
              size="extra-small"
              variant="secondary"
            />
          </div>
        ),
        className: 'text-end',
        key: 'actions',
      },
    ];
  };

  return (
    <ListTable<ReconciliationItem>
      headers={tableHeaders}
      data={data}
      rowBuilder={buildRow}
      rowClickPath={(item) => routeToPage(PaymentReconciliationRoutes.detail, { id: item.id })}
      isLoading={isLoading || (differentFilters && emptyData)}
      pagination={
        <InfiniteScroll
          hasMore={Boolean(hasNextPage)}
          loadMore={fetchNextPage}
          loading={isFetchingNextPage}
          noMoreItemsEnabled={Boolean(hasPreviousPage)}
        />
      }
      emptyState={
        <ListTable.EmptyState
          background="gray"
          cols={tableHeaders.length}
          cta={
            hasFilters
              ? {
                  children: t('common:table.withFilters.emptyState.cta'),
                  onClick: clearAllFilters,
                  analyticsId: 'paymentReconciliation:listPage.openTab.list.clear_filters',
                }
              : undefined
          }
        >
          {hasFilters
            ? t('common:table.withFilters.emptyState.title')
            : t('paymentReconciliation:listPage.openTab.list.emptyState')}
        </ListTable.EmptyState>
      }
    />
  );
};
